/** @format */
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MaxLength } from 'common-typescript/constants';
import { LocalizedString, Module } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { combineLatest, from, merge, Observable, of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { LocaleService } from 'sis-common';
import { ConfirmDialogValues } from '../../confirm/confirm-dialog.component';
import { ConfirmDialogService } from '../../confirm/confirm-dialog.service';
import { AppErrorHandler } from '../../error-handler/app-error-handler';
import { SisFormBuilder } from '../../form/sis-form-builder.service';
import { AttainmentEntityService } from '../../service/attainment-entity.service';
import { AttainmentTreeService } from './attainment-tree.service';
import {
    CollapsedByGroupingModuleId,
    CourseUnitsById,
    IndexedAttainments,
    ModulesById,
    ModuleSelectionWithOrdering,
    PlanWithOrdering,
    SelectionItem,
} from './AttainmentTreeTypes';

@Component({
    selector: 'sis-editable-attainment-tree-grouping-module',
    templateUrl: './editable-attainment-tree-grouping-module.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class EditableAttainmentTreeGroupingModuleComponent implements OnInit {
    constructor(
        private appErrorHandler: AppErrorHandler,
        private attainmentTreeService: AttainmentTreeService,
        private attainmentEntityService: AttainmentEntityService,
        private localeService: LocaleService,
        private fb: SisFormBuilder,
        private confirmDialogService: ConfirmDialogService,
        private translateService: TranslateService,
    ) {}

    @Input() moduleSelection: ModuleSelectionWithOrdering;
    @Input() moduleContent: PlanWithOrdering;
    @Input() indexedAttainments: IndexedAttainments;
    @Input() courseUnitsById: CourseUnitsById;
    @Input() modulesById: ModulesById;

    @Input() set selectedLocale(selectedLocale: string) {
        this.privateSelectedLocale = selectedLocale || this.privateSelectedLocale;
        this._selectedLocale = selectedLocale;
    }

    get selectedLocale(): string {
        return this._selectedLocale;
    }

    @Input() collapsedByGroupingModuleId: CollapsedByGroupingModuleId;
    // this emits events upwards in component tree - see explanation in EditableAttainmentTreeRootComponent
    @Output() childrenUpdated = new EventEmitter<void>();
    // this emits events downwards in component tree - see explanation in EditableAttainmentTreeRootComponent
    @Input() groupingModuleVisibilityToggled: Observable<void>;
    _selectedLocale: string;
    privateSelectedLocale: string = this.localeService.getCurrentLanguage();
    nameEditMode = false;
    newName: FormGroup;
    module: Module;
    originalNameTextForTooltip: string;
    ownGroupingModuleVisibilityToggledSubject = new Subject<void>();
    mergedGroupingModuleVisibilityToggled: Observable<void>;
    nameIsOriginal: boolean;

    ngOnInit(): void {
        this.mergedGroupingModuleVisibilityToggled = merge(
            this.ownGroupingModuleVisibilityToggledSubject,
            this.groupingModuleVisibilityToggled,
        );
        this.module = this.modulesById[this.moduleSelection.moduleId];
        this.nameIsOriginal = this.areLocalizedStringsEqualOnView(this.moduleSelection.newName, this.module.name);
        this.originalNameTextForTooltip = [
            this.translateService.instant('ATTAINMENTS.EDITABLE_ATTAINMENT_TREE.ORIGINAL_GROUPING_MODULE_NAME_TEXT'),
            ..._.map(this.localeService.getOfficialLanguages(), locale => `${locale}: ${_.get(this.module, ['name', locale], '')}`),
        ].join('\n');
    }

    unRemove() {
        const children = this.attainmentTreeService.getChildrenNotDirectlyAfterModule(this.moduleSelection, this.moduleContent);
        if (_.isEmpty(children)) {
            this.privateUnRemove();
            return;
        }
        // Here we ensure that children have attainments attached. Then we use these attainments to get codes and names.
        this.attainmentTreeService.addAttainmentsToSelectionItems(
            children,
            this.indexedAttainments,
            this.modulesById,
            this.courseUnitsById,
        );
        combineLatest(children.map(child => this.selectionItemToName(child)))
            .pipe(
                switchMap(names => this.getModal(names)),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe({
                next: () => this.privateUnRemove(),
            });
    }

    getModal(names: string[]): Observable<void> {
        const values: ConfirmDialogValues<any> = {
            title: 'ATTAINMENTS.EDITABLE_ATTAINMENT_TREE.SHOW_GM_MODAL.TITLE',
            descriptionSets: [
                {
                    name: this.localeService.localize(this.moduleSelection.newName),
                    descriptions: [
                        'ATTAINMENTS.EDITABLE_ATTAINMENT_TREE.SHOW_GM_MODAL.DECRIPTION_MAIN',
                        'ATTAINMENTS.EDITABLE_ATTAINMENT_TREE.SHOW_GM_MODAL.DECRIPTION_STUDIES',
                    ],
                },
                { objects: names },
                { descriptions: ['ATTAINMENTS.EDITABLE_ATTAINMENT_TREE.SHOW_GM_MODAL.DECRIPTION_QUESTION'] },
            ],
        };
        return from(this.confirmDialogService.confirm(values));
    }

    selectionItemToName(child: SelectionItem): Observable<string> {
        if (child.type === 'GroupingModuleLike') {
            return of(this.localeService.localize((child.studySelection as ModuleSelectionWithOrdering).newName));
        }
        const attainmentName$ = this.attainmentEntityService
            .getAttainmentName(child.attainment)
            .pipe(map(name => this.localeService.localize(name) as string));
        return combineLatest([this.attainmentEntityService.getAttainmentCode(child.attainment), attainmentName$]).pipe(
            map(texts => texts.join(' ')),
        );
    }

    privateUnRemove() {
        this.moduleSelection.hidden = false;
        this.attainmentTreeService.updateOrderingFor(this.moduleSelection, this.moduleContent);
        this.childrenUpdated.emit();
        this.ownGroupingModuleVisibilityToggledSubject.next();
    }

    remove() {
        this.moduleSelection.hidden = true;
        this.attainmentTreeService.updateOrderingFor(this.moduleSelection, this.moduleContent);
        this.childrenUpdated.emit();
        this.ownGroupingModuleVisibilityToggledSubject.next();
    }

    editName() {
        this.newName = this.fb.localizedString(this.moduleSelection.newName, {
            oneRequired: true,
            maxLength: MaxLength.MAX_TERSE_STRING_LENGTH,
            minLength: 1,
        });
        this.nameEditMode = true;
    }

    saveName() {
        if (!this.newName.valid) {
            return;
        }
        this.moduleSelection.newName = this.newName.value;
        this.nameIsOriginal = this.areLocalizedStringsEqualOnView(this.moduleSelection.newName, this.module.name);
        this.nameEditMode = false;
    }

    areLocalizedStringsEqualOnView(value1: LocalizedString, value2: LocalizedString): boolean {
        return _.every(
            this.localeService.getOfficialLanguages(),
            locale => (_.get(value1, locale) || '') === (_.get(value2, locale) || ''),
        );
    }
}
